import { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, X as XIcon, Loader2, ArrowUp, ArrowDown, ShieldCheck, Trash2, RotateCcw, Clock } from "lucide-react";
import DeletionPreviewModal from "../components/DeletionPreviewModal";
import DowngradePreviewModal from "../components/DowngradePreviewModal";
import ReactivateRestoreModal from "../components/ReactivateRestoreModal";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { confirmDialog } from "../lib/confirm";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDate, deDateFull, deDateMedium, deDateShortTime } from "../lib/dateFormat";

/**
 * Feb 2026 · Directive „PIN-Gate für Rollenänderung + Last-Admin-Schutz".
 *
 * Kleines inlines PIN-Modal für die Rollen-Änderung. Bewusst getrennt vom
 * globalen `PinUnlockDialog` — dessen Zweck ist die Concierge-Impersonations-
 * Freischaltung, und die User-Direktive zum Rollen-Switcher hat den globalen
 * Dialog explizit ausgeschlossen. Hier läuft ein dedizierter, klar
 * beschrifteter Prompt.
 *
 * Der PIN wird direkt an den PATCH-Endpoint mitgegeben (`admin_pin`-Feld);
 * das Backend verifiziert serverseitig und wirft 403 bei Missmatch.
 */
function RoleChangePinModal({ open, target, customers, onCancel, onSubmit }) {
  const [pin, setPin] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [busy, setBusy] = useState(false);
  const needsOwner = target?.newRole === "family";
  useEffect(() => {
    if (open) { setPin(""); setOwnerId(target?.currentOwnerId || ""); }
  }, [open, target?.currentOwnerId]);
  if (!open) return null;
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{4}$/.test(pin)) {
      toast.error("Bitte 4 Ziffern eingeben.");
      return;
    }
    if (needsOwner && !ownerId) {
      toast.error("Familie muss mit einem Kunden verknüpft sein. Bitte einen Hauptkunden auswählen.");
      return;
    }
    setBusy(true);
    try {
      await onSubmit(pin, needsOwner ? ownerId : null);
    } finally {
      setBusy(false);
    }
  };
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: withAlpha(BRAND_COLORS.navy, 0.55) }}
      onClick={onCancel}
      data-testid="role-change-pin-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-3 right-3 text-navy-dark/50 hover:text-navy"
          aria-label="Schließen"
          data-testid="role-change-pin-close"
        >
          <XIcon className="w-4 h-4" strokeWidth={1.6} />
        </button>
        <form onSubmit={submit} className="p-6 md:p-8">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
            style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}
          >
            <KeyRound className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <h2 className="font-display text-2xl text-navy leading-tight mb-1">
            Admin-PIN erforderlich
          </h2>
          <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-5">
            Rolle von <b>{target?.email}</b> ändern<br />
            <span className="text-navy-dark/60">
              {target?.oldRole} → {target?.newRole}
            </span>
            <br />
            Zur Bestätigung bitte die 4-stellige Bearbeitungs-PIN eingeben.
          </p>
          {needsOwner && (
            <div className="mb-5" data-testid="role-change-owner-block">
              <label className="label-caps block mb-2" style={{ color: BRAND_COLORS.gold }}>
                Hauptkunde (Pflicht)
              </label>
              <select
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
                data-testid="role-change-owner-select"
                required
                className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-[14px] text-navy bg-white"
                disabled={busy}
              >
                <option value="">— Hauptkunde wählen —</option>
                {(customers || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name ? `${c.name} (${c.email})` : c.email}
                  </option>
                ))}
              </select>
              <p className="text-[11.5px] text-navy-dark/55 mt-2 leading-snug">
                Familie-Zugänge müssen immer einem Kunden zugeordnet sein.
              </p>
            </div>
          )}
          <label className="label-caps block mb-2" style={{ color: BRAND_COLORS.gold }}>
            Bearbeitungs-PIN
          </label>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            data-testid="role-change-pin-input"
            className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-[18px] tracking-[0.5em] text-center text-navy tabular-nums"
            placeholder="••••"
            autoFocus
            disabled={busy}
          />
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              data-testid="role-change-pin-cancel"
              className="text-[11px] tracking-[0.15em] uppercase text-navy-dark/60 hover:text-navy px-3 py-2"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy || pin.length !== 4 || (needsOwner && !ownerId)}
              data-testid="role-change-pin-submit"
              className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
              style={{ background: BRAND_COLORS.navy }}
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
              Bestätigen
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}


export default function TeamAllowed() {
  const { t } = useI18n();
  const { user, effectiveRole } = useAuth();
  const [rows, setRows] = useState([]);
  const [mainCustomersMap, setMainCustomersMap] = useState({ family: {}, partners: {} });
  // Feb 2026 · 8 Sep 2026 · User-Directive „Familie muss mit Kunde verknüpft sein":
  // Kunden-Liste für den Family-Owner-Selector im Anlege-Formular und im
  // Rollen-Wechsel-Modal. Wir laden alle aktiven Kunden einmal beim Mount.
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ email: "", role: "customer", name: "", owner_id: "" });
  const [loading, setLoading] = useState(false);
  // Feb 2026 · DSGVO / Gast-Tier Phase 3 (5 Sep 2026 · User-Directive):
  // Status-Filter (Alle / Gast / Abonnenten / Abgelaufen). Der Filter
  // wirkt nur auf role=customer + role=guest — Team-Rollen bleiben
  // immer sichtbar (sonst verschwindet der Admin selbst aus der Liste).
  const [statusFilter, setStatusFilter] = useState("all"); // all | guest | subscriber | expired
  // Feb 2026 · PIN-Modal-State für Rollen-Änderung.
  const [pinModal, setPinModal] = useState(null); // { row, newRole, oldRole } | { row, kind: "upgrade" }
  // Feb 2026 · DSGVO Art. 17 · Preview-Modal (5 Sep 2026 · User-Directive
  // „Preview vor PIN, um versehentliche Loeschungen fast unmoeglich zu
  // machen").
  const [downgradeActivity, setDowngradeActivity] = useState(null); // { count_24h, over_threshold }
  const [previewModal, setPreviewModal] = useState(null); // { row }
  // Feb 2026 · 6 Sep 2026 · Downgrade-Preview vor PIN + Reactivate-Restore
  const [downgradePreview, setDowngradePreview] = useState(null); // { row }
  const [restoreModal, setRestoreModal] = useState(null); // { row }
  // Feb 2026 · 17 Sep 2026 · Bulk-Onboarding-Mail-Kadenz — Serien-Status
  // pro allowed_email. Geladen aus `/api/admin/onboarding-cadence`, gemappt
  // per email für O(1)-Lookup in der Row-Renderer-Schleife.
  const [cadenceByEmail, setCadenceByEmail] = useState({});
  const [cadenceBusy, setCadenceBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/allowed-emails");
    setRows(data);
    // Feb 2026 · Verbundene Zug\u00e4nge (5 Sep 2026 · User-Directive) \u2014
    // Umgekehrte Sicht: fuer jede Family-/Partner-Zeile den Hauptkunden
    // laden, damit im Zugangs-Profil klar sichtbar ist, zu wem der
    // Nutzer gehoert. Ein einziger API-Call reicht.
    try {
      const { data: map } = await api.get("/team/allowed/main-customers");
      setMainCustomersMap(map || { family: {}, partners: {} });
    } catch {
      setMainCustomersMap({ family: {}, partners: {} });
    }
    // Feb 2026 · 8 Sep 2026 · Family-Owner-Selector — Kunden-Liste laden.
    try {
      const { data: custs } = await api.get("/team/customers");
      setCustomers(Array.isArray(custs) ? custs.filter((c) => c.role === "customer") : []);
    } catch {
      setCustomers([]);
    }
    // Feb 2026 · 17 Sep 2026 · Onboarding-Kadenz-Status.
    try {
      const { data: cad } = await api.get("/admin/onboarding-cadence");
      const map = {};
      for (const row of cad.rows || []) {
        if (row.email) map[row.email] = row;
      }
      setCadenceByEmail(map);
    } catch {
      setCadenceByEmail({});
    }
  };
  useEffect(() => { load(); }, []);

  // Feb 2026 · 17 Sep 2026 · Admin-Trigger für Onboarding-Sweep (Tag 3 + Tag 7).
  const dispatchOnboarding = async () => {
    setCadenceBusy(true);
    try {
      const { data } = await api.post("/admin/onboarding-cadence/dispatch");
      const s = data?.stats || {};
      toast.success(
        `Onboarding-Sweep · Tag 3: ${s.day3_sent || 0} · Tag 7: ${s.day7_sent || 0}`,
      );
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setCadenceBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Feb 2026 · 8 Sep 2026 · User-Directive „Familie muss mit Kunde verknüpft sein":
    // Client-side Guard — verhindert einen unnötigen 400-Roundtrip und liefert
    // sofort Feedback, wenn der Admin die Zuordnung vergisst.
    if (form.role === "family" && !form.owner_id) {
      toast.error("Familie muss mit einem Kunden verknüpft sein. Bitte einen Hauptkunden auswählen.");
      return;
    }
    setLoading(true);
    try {
      const payload = { email: form.email, role: form.role, name: form.name };
      if (form.role === "family" && form.owner_id) payload.owner_id = form.owner_id;
      await api.post("/admin/allowed-emails", payload);
      toast.success("E-Mail freigeschaltet.");
      setForm({ email: "", role: "customer", name: "", owner_id: "" });
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Feb 2026 · Directive „Last-Admin-Schutz": Client-side Guard. Backend
  // enforced dasselbe für den harten Schutz — hier ist die Kontrolle nur
  // dazu da, die UI vorab zu sperren und dem Admin klar zu signalisieren.
  // Feb 2026 · 6 Sep 2026 · Verschaerft auf „mindestens 2 Admins":
  // Bei genau 2 Admins darf keiner heruntergestuft/entfernt werden.
  const adminCount = rows.filter((r) => r.role === "admin").length;
  const isLastAdmin = (r) => r.role === "admin" && adminCount <= 2;

  const remove = async (r) => {
    if (isLastAdmin(r)) {
      toast.error("Mindestens 2 Admins muessen im System bleiben.");
      return;
    }
    // Feb 2026 · 6 Sep 2026 · User-Directive „Admin nicht direkt loeschbar":
    // Admins koennen nicht direkt entfernt werden — sie muessen erst auf
    // 'Concierge' herabgestuft werden.
    if (r.role === "admin") {
      toast.error("Admins koennen nicht direkt entfernt werden. Bitte zuerst auf 'Concierge' herabstufen.");
      return;
    }
    // Feb 2026 · 8 Sep 2026 · User-Directive „Kunde nicht direkt entfernen":
    // „Ein Nutzer mit der Rolle 'Kunde' darf nicht direkt entfernt werden.
    // Der einzig erlaubte Weg ist: Kunde → Gast (mit PIN) → Entfernen."
    if (r.role === "customer") {
      toast.error("Kunden koennen nicht direkt entfernt werden. Bitte zuerst auf 'Gast' herabstufen.");
      return;
    }
    // Feb 2026 · DSGVO Art. 17 + §147 AO · Admin-Deletion (5 Sep 2026 · User-Directive)
    // Fuer Kunden/Gaeste/Concierges/Partner: soft-delete mit 30 Tagen
    // Karenz, PIN-geschuetzt. Admin/Family: existierender Hard-Delete.
    const SOFT_ROLES = new Set(["customer", "guest", "concierge", "partner"]);
    if (SOFT_ROLES.has(r.role)) {
      // Feb 2026 · Preview-Modal statt confirmDialog: transparente
      // Anzeige, was beibehalten und was anonymisiert wird — dann PIN.
      setPreviewModal({ row: r });
      return;
    }
    // Legacy hard-delete für Admin/Family
    if (!(await confirmDialog({
      title: "Zugang entfernen?",
      description: "Der Nutzer-Account und offene OTP-Codes werden mit gelöscht.\nAnfragen, Buchungen und Objekte bleiben erhalten.",
      confirmText: "Entfernen",
      variant: "danger",
    }))) return;
    try {
      const { data } = await api.delete(`/admin/allowed-emails/${r.id}`);
      const parts = [];
      if (data.users_removed) parts.push(`${data.users_removed} Nutzer`);
      if (data.otp_codes_removed) parts.push(`${data.otp_codes_removed} offene OTP-Codes`);
      toast.success(parts.length ? `Zugang entfernt · ${parts.join(" · ")} mit aufgeräumt.` : "Zugang entfernt.");
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  const reactivate = async (r) => {
    if (!(await confirmDialog({
      title: "Löschung rückgängig machen?",
      description: `Die Löschung von ${r.email} wird gestoppt. Der Account kann wieder normal genutzt werden. Concierge-Zuweisungen werden NICHT automatisch wiederhergestellt.\n\nBestätigung mit Admin-PIN.`,
      confirmText: "Reaktivieren",
    }))) return;
    setPinModal({ row: r, kind: "reactivate" });
  };

  const submitRoleChange = async (pin, ownerId) => {
    if (!pinModal) return;
    // Feb 2026 · DSGVO / Gast-Tier Phase 3 · Upgrade-Flow (5 Sep 2026).
    if (pinModal.kind === "upgrade") {
      try {
        const { data } = await api.post(
          `/admin/allowed-emails/${pinModal.row.id}/upgrade-guest`,
          { admin_pin: pin },
        );
        toast.success(
          data.users_updated
            ? `Gast hochgestuft · ${data.users_updated} Konto zu „Abonnent" umgestellt.`
            : "Gast hochgestuft zu Abonnent.",
        );
        // Feb 2026 · 6 Sep 2026 · Reactivate-Restore-Flow:
        // Nach erfolgreichem Upgrade pruefen, ob Vorgaenge zum
        // Wiederherstellen da sind — wenn ja, Modal oeffnen.
        setPinModal(null);
        setRestoreModal({ row: pinModal.row });
        await load();
      } catch (err) {
        const status = err.response?.status;
        if (status === 403) {
          toast.error("PIN ist falsch. Bitte erneut eingeben.");
        } else {
          toast.error(formatErr(err.response?.data?.detail) || err.message);
          setPinModal(null);
          await load();
        }
      }
      return;
    }
    // Feb 2026 · DSGVO Art. 17 · Admin-Deletion (5 Sep 2026 · User-Directive)
    if (pinModal.kind === "delete") {
      try {
        const { data } = await api.post(
          `/admin/allowed-emails/${pinModal.row.id}/delete`,
          { admin_pin: pin },
        );
        if (data.status === "hard_removed") {
          toast.success("Zugang entfernt (nie eingeloggt — keine Karenz nötig).");
        } else {
          const iso = data.execute_at;
          const when = iso ? deDateMedium(iso) : "in 30 Tagen";
          const reset = data.assignments_reset;
          const resetParts = [];
          if (reset?.requests) resetParts.push(`${reset.requests} Anfragen`);
          if (reset?.bookings) resetParts.push(`${reset.bookings} Buchungen`);
          if (reset?.tasks) resetParts.push(`${reset.tasks} Aufgaben`);
          const resetSuffix = resetParts.length ? ` · ${resetParts.join(" · ")} auf „nicht zugeteilt" gesetzt` : "";
          toast.success(`Konto deaktiviert · Endgültige Löschung am ${when}${resetSuffix}.`);
        }
        setPinModal(null);
        await load();
      } catch (err) {
        const status = err.response?.status;
        if (status === 403) {
          toast.error("PIN ist falsch. Bitte erneut eingeben.");
        } else {
          toast.error(formatErr(err.response?.data?.detail) || err.message);
          setPinModal(null);
          await load();
        }
      }
      return;
    }
    if (pinModal.kind === "reactivate") {
      try {
        await api.post(
          `/admin/allowed-emails/${pinModal.row.id}/reactivate`,
          { admin_pin: pin },
        );
        toast.success("Konto reaktiviert. Der Nutzer kann sich wieder einloggen.");
        setPinModal(null);
        await load();
      } catch (err) {
        const status = err.response?.status;
        if (status === 403) {
          toast.error("PIN ist falsch. Bitte erneut eingeben.");
        } else {
          toast.error(formatErr(err.response?.data?.detail) || err.message);
          setPinModal(null);
          await load();
        }
      }
      return;
    }
    // (5 Sep 2026 · User-Directive „PIN-geschuetzt, Badge in Uebersicht").
    if (pinModal.kind === "finance-toggle") {
      try {
        const { data } = await api.post(
          `/admin/users/${pinModal.row.user_id}/finance-admin-toggle`,
          { admin_pin: pin, enabled: !pinModal.row.is_finance_admin },
        );
        toast.success(
          data.is_finance_admin
            ? "Finance-Admin aktiviert."
            : "Finance-Admin deaktiviert.",
        );
        setPinModal(null);
        await load();
      } catch (err) {
        const status = err.response?.status;
        if (status === 403) {
          toast.error(
            err.response?.data?.detail?.includes("Owner")
              ? (err.response?.data?.detail || "Nur der Owner darf Finance-Admins schalten.")
              : "PIN ist falsch. Bitte erneut eingeben.",
          );
        } else {
          toast.error(formatErr(err.response?.data?.detail) || err.message);
          setPinModal(null);
          await load();
        }
      }
      return;
    }
    // Feb 2026 · DSGVO / Gast-Tier · Downgrade-Flow (5 Sep 2026 · User-Directive):
    // „Admin kann Kunden manuell auf Gast herunterstufen — z.B. wenn Abo
    // abgelaufen ist. Nach Herabstufung gelten sofort die Gast-Locks."
    if (pinModal.kind === "downgrade") {
      try {
        const { data } = await api.post(
          `/admin/allowed-emails/${pinModal.row.id}/downgrade-to-guest`,
          { admin_pin: pin },
        );
        // Feb 2026 · 6 Sep 2026 · User-Directive „Nach Downgrade automatisch
        // stornieren": Wenn Backend Buchungen/Anfragen storniert hat, zeigen
        // wir den Cascade-Count als zusaetzliche Zeile im Toast, damit der
        // Admin weiss, was mit den offenen Vorgaengen passiert ist.
        const cascade = data.cascade;
        const cascadeParts = [];
        if (cascade?.bookings_cancelled) cascadeParts.push(`${cascade.bookings_cancelled} Buchungen storniert`);
        if (cascade?.requests_cancelled) cascadeParts.push(`${cascade.requests_cancelled} Anfragen storniert`);
        // Feb 2026 · 9 Sep 2026 · Objektverwaltung: Objekte werden beim Downgrade
        // in „Entfernte Objekte" archiviert (nicht storniert). Der Toast trennt
        // die beiden Zustände optisch.
        if (cascade?.properties_archived) cascadeParts.push(`${cascade.properties_archived} Objekte archiviert`);
        const cascadeSuffix = cascadeParts.length
          ? ` · Automatisch: ${cascadeParts.join(", ")}`
          : "";
        toast.success(
          (data.users_updated
            ? `Auf Gast herabgestuft · ${data.users_updated} Konto umgestellt.`
            : "Auf Gast herabgestuft.") + cascadeSuffix,
        );
        setPinModal(null);
        await load();
      } catch (err) {
        const status = err.response?.status;
        if (status === 403) {
          toast.error("PIN ist falsch. Bitte erneut eingeben.");
        } else {
          toast.error(formatErr(err.response?.data?.detail) || err.message);
          setPinModal(null);
          await load();
        }
      }
      return;
    }
    try {
      const patchBody = { role: pinModal.newRole, admin_pin: pin };
      // Feb 2026 · 8 Sep 2026 · User-Directive „Familie muss mit Kunde verknüpft sein":
      // Bei Wechsel auf 'family' schicken wir owner_id mit — Backend rejected sonst 400.
      if (pinModal.newRole === "family" && ownerId) patchBody.owner_id = ownerId;
      const { data } = await api.patch(
        `/admin/allowed-emails/${pinModal.row.id}/role`,
        patchBody,
      );
      // Feb 2026 · 6 Sep 2026 · User-Directive „Concierge darf kein Objekt besitzen":
      // Falls Backend Objekte/Buchungen/Anfragen kaskadiert entfernt hat,
      // zeigen wir den Cascade-Count als zusaetzliche Zeile im Toast, damit
      // der Admin Transparenz ueber Datenverlust hat.
      const cascade = data.cascade;
      const cascadeParts = [];
      if (cascade?.properties_removed) cascadeParts.push(`${cascade.properties_removed} Objekte`);
      if (cascade?.bookings_removed) cascadeParts.push(`${cascade.bookings_removed} Buchungen`);
      if (cascade?.requests_removed) cascadeParts.push(`${cascade.requests_removed} Anfragen`);
      const cascadeSuffix = cascadeParts.length
        ? ` · Concierge-Regel: ${cascadeParts.join(" · ")} entfernt`
        : "";
      toast.success(
        (data.users_updated
          ? `Rolle geändert · ${data.users_updated} Nutzer-Account aktualisiert.`
          : "Rolle geändert.") + cascadeSuffix,
      );
      setPinModal(null);
      await load();
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        toast.error("PIN ist falsch. Bitte erneut eingeben.");
      } else {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
        setPinModal(null);
        await load();
      }
    }
  };

  const isAdmin = effectiveRole === "admin";

  // Feb 2026 · DSGVO / Gast-Tier Phase 3 (5 Sep 2026): Filter-Chips
  // wirken nur auf customer + guest — Team-Rollen (admin/concierge/
  // partner) und Family bleiben immer eingeblendet. So verschwindet
  // niemand aus der Liste, wenn der Admin auf „Gast" filtert.
  const STATUS_META = {
    all: { label: "Alle", color: BRAND_COLORS.navy },
    guest: { label: "Gast", color: BRAND_COLORS.gold },
    subscriber: { label: "Abonnenten", color: BRAND_COLORS.greenDeep },
    expired: { label: "Abgelaufen", color: "#8B1E1E" },
  };
  const counts = useMemo(() => {
    const c = { all: rows.length, guest: 0, subscriber: 0, expired: 0 };
    for (const r of rows) {
      const s = r.status;
      if (s === "guest") c.guest += 1;
      else if (s === "subscriber") c.subscriber += 1;
      else if (s === "expired") c.expired += 1;
    }
    return c;
  }, [rows]);
  const visibleRows = useMemo(() => {
    // Feb 2026 · 8 Sep 2026 · User-Directive „Sortiere alle Zugänge nach Rolle:
    // Admin → Concierge → Kunde (mit angehöriger Familie) → Gast → Partner".
    // Familien-Einträge werden direkt UNTER ihrem Hauptkunden einsortiert
    // (via `mainCustomersMap.family[email].main_customers[0].email`). Innerhalb
    // jeder Rollen-Gruppe sortieren wir nach Name/E-Mail alphabetisch, damit
    // die Anzeige deterministisch bleibt.
    const filtered = statusFilter === "all"
      ? rows
      : rows.filter((r) => {
          if (["admin", "concierge", "partner"].includes(r.role)) return true;
          return r.status === statusFilter;
        });

    const byName = (a, b) => {
      const an = (a.name || a.email || "").toLowerCase();
      const bn = (b.name || b.email || "").toLowerCase();
      return an.localeCompare(bn);
    };

    const admins = filtered.filter((r) => r.role === "admin").sort(byName);
    const concierges = filtered.filter((r) => r.role === "concierge").sort(byName);
    const customers = filtered.filter((r) => r.role === "customer").sort(byName);
    const families = filtered.filter((r) => r.role === "family");
    const guests = filtered.filter((r) => r.role === "guest").sort(byName);
    const partners = filtered.filter((r) => r.role === "partner").sort(byName);
    const unknown = filtered.filter(
      (r) => !["admin", "concierge", "customer", "family", "guest", "partner"].includes(r.role),
    ).sort(byName);

    // Bucket family entries by their main customer's e-mail (case-insensitive).
    // Orphan families (kein Hauptkunde bekannt) rutschen ans Ende des Kunden-
    // Blocks, damit sie nicht unsichtbar werden.
    const familyBucket = {};
    const orphanFamilies = [];
    for (const f of families) {
      const mainCustomers = (mainCustomersMap.family || {})[f.email]?.main_customers || [];
      if (mainCustomers.length > 0) {
        const key = (mainCustomers[0].email || "").toLowerCase();
        (familyBucket[key] = familyBucket[key] || []).push(f);
      } else {
        orphanFamilies.push(f);
      }
    }
    orphanFamilies.sort(byName);
    for (const k of Object.keys(familyBucket)) familyBucket[k].sort(byName);

    // Interleave: pro Kunde direkt danach dessen Familien-Einträge.
    const customerBlock = [];
    for (const c of customers) {
      customerBlock.push(c);
      const kids = familyBucket[(c.email || "").toLowerCase()] || [];
      customerBlock.push(...kids);
    }
    // Familien ohne bekannten Hauptkunden bleiben am Ende des Kunden-Blocks.
    customerBlock.push(...orphanFamilies);

    return [...admins, ...concierges, ...customerBlock, ...guests, ...partners, ...unknown];
  }, [rows, statusFilter, mainCustomersMap]);
  const filteredList = visibleRows;

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-allowed">
      <div className="mb-10">
        <div className="label-caps mb-3">Weissblom Team</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">{t.team.allowed_title}</h1>
        <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">{t.team.allowed_lead}</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-navy/10 p-6 grid grid-cols-1 md:grid-cols-5 gap-4 mb-4" data-testid="allowed-form">
        <input
          data-testid="allowed-email-input"
          required type="email" placeholder="E-Mail"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
        />
        <input
          data-testid="allowed-name-input"
          placeholder="Name (optional)"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
        />
        <select
          data-testid="allowed-role-select"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value, owner_id: e.target.value === "family" ? form.owner_id : "" })}
          className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
        >
          <option value="customer">Kunde</option>
          <option value="family">Familie</option>
          <option value="concierge">Concierge · Team</option>
          <option value="admin">Admin · Team</option>
          <option value="partner">Partner</option>
        </select>
        <button
          data-testid="allowed-submit-btn"
          type="submit" disabled={loading || (form.role === "family" && !form.owner_id)}
          className="bg-navy text-ivory py-3 text-[12px] tracking-[0.15em] uppercase hover:bg-navy-dark transition-colors disabled:opacity-50"
          style={{ background: BRAND_COLORS.navy }}
        >
          {loading ? t.common.loading : t.team.add_email}
        </button>
        {/* Feb 2026 · 8 Sep 2026 · User-Directive „Familie muss mit Kunde verknüpft sein":
            Wenn Rolle 'Familie' ausgewählt ist, muss der Admin einen Hauptkunden
            wählen. Ohne Auswahl bleibt der Submit-Button disabled + Backend
            liefert 400 als zweite Verteidigungslinie. */}
        {form.role === "family" && (
          <div className="md:col-span-5" data-testid="allowed-family-owner-block">
            <label className="label-caps block mb-2" style={{ color: BRAND_COLORS.gold }}>
              Hauptkunde (Pflicht für Familie)
            </label>
            <select
              value={form.owner_id}
              onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
              required
              data-testid="allowed-family-owner-select"
              className="w-full md:w-1/2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
            >
              <option value="">— Hauptkunde wählen —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name ? `${c.name} (${c.email})` : c.email}
                </option>
              ))}
            </select>
            <p className="text-[11.5px] text-navy-dark/55 mt-2 leading-snug">
              Familien-Zugänge müssen immer einem Kunden zugeordnet sein.
              Ohne Verknüpfung wird die Aktion vom System blockiert.
            </p>
          </div>
        )}
      </form>
      {/* Feb 2026 · 6 Sep 2026 · User-Directive „Bulk-Neuer-Team-Zugang-Onboarding":
          Info-Hint direkt unter dem Formular, wenn Team-Rolle gewaehlt ist —
          Admin weiss dann, dass die Willkommens-Mail automatisch geschickt wird
          und der neue Zugang mit dem gemeinsamen Admin-PIN startet. */}
      {(form.role === "admin" || form.role === "concierge") && (
        <div
          data-testid="allowed-team-onboarding-hint"
          className="mb-8 flex items-start gap-3 border-l-2 pl-4 py-2 text-[12.5px]"
          style={{ borderColor: BRAND_COLORS.gold, color: BRAND_COLORS.navyDark }}
        >
          <span className="uppercase tracking-[0.2em] text-[10px]" style={{ color: BRAND_COLORS.goldDark }}>Team-Zugang</span>
          <span className="leading-snug">
            {form.role === "admin"
              ? "Ein Admin hat vollen Zugriff auf Team-Verwaltung + Finance. Nach dem Anlegen wird automatisch eine Willkommens-Mail mit Login-Link versendet; der neue Admin nutzt den gemeinsamen 4-stelligen Admin-PIN."
              : "Ein Concierge kann Buchungen, Anfragen und Kalender bearbeiten, aber nicht das Team verwalten. Nach dem Anlegen wird automatisch eine Willkommens-Mail versendet; PIN-Aktionen erfordern die Admin-PIN eines Admins."}
          </span>
        </div>
      )}
      {form.role !== "admin" && form.role !== "concierge" && <div className="mb-8" />}

      {/* Feb 2026 · 6 Sep 2026 · User-Directive „Massen-Downgrade-Prevention-Banner":
          Wenn der aktuelle Admin in den letzten 24h mehr als 3 Kunden downgegradet
          hat, zeigen wir einen roten Warnhinweis, damit ungewoehnliche Aktivitaet
          nicht unbemerkt durchgeht. */}
      {downgradeActivity?.over_threshold && (
        <div
          data-testid="mass-downgrade-warning-banner"
          className="mb-6 border-l-4 pl-5 pr-4 py-4 flex items-start gap-4"
          style={{ borderColor: "#B33636", background: "rgba(179,54,54,0.06)" }}
        >
          <ArrowDown className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={1.8} style={{ color: "#B33636" }} />
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase mb-1" style={{ color: "#8A2828" }}>
              Erhoehte Downgrade-Aktivitaet
            </div>
            <div className="text-[13.5px] text-navy leading-snug">
              Sie haben in den letzten 24 Stunden <b className="tabular-nums" data-testid="mass-downgrade-count">{downgradeActivity.count_24h}</b> Kunden auf Gast heruntergestuft — mehr als der Schwellenwert von {downgradeActivity.threshold}.
            </div>
            <div className="text-[12px] text-navy-dark/70 leading-snug mt-2 italic">
              Bitte das <b>Support-Team</b> benachrichtigen, falls diese Menge nicht beabsichtigt war. Weitere Downgrades bleiben ausfuehrbar.
            </div>
          </div>
        </div>
      )}

      {/* Feb 2026 · 6 Sep 2026 · User-Directive „Admin-Ban-Countdown":
          Wenn genau 2 Admins im System sind, erscheint oben ein Warnbanner —
          jede weitere Herabstufung/Loeschung ist blockiert bis mindestens
          ein weiterer Admin eingerichtet wurde. */}
      {adminCount === 2 && (
        <div
          data-testid="admin-count-warning-banner"
          className="mb-6 border-l-4 pl-5 pr-4 py-4 flex items-start gap-4"
          style={{ borderColor: BRAND_COLORS.gold, background: withAlpha(BRAND_COLORS.gold, 0.08) }}
        >
          <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={1.6} style={{ color: BRAND_COLORS.goldDark }} />
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase mb-1" style={{ color: BRAND_COLORS.goldDark }}>
              Sicherheits-Untergrenze erreicht
            </div>
            <div className="text-[13.5px] text-navy leading-snug">
              Es sind aktuell nur <b className="tabular-nums">2 Admins</b> im System. Ein weiterer Rollenwechsel oder eine Loeschung ist nicht mehr moeglich, bis mindestens ein <b>3. Admin</b> eingerichtet wurde.
            </div>
            <div className="text-[12px] text-navy-dark/70 leading-snug mt-2 italic">
              Loesung: Neue E-Mail im Formular oben mit Rolle „Admin" freischalten &mdash; die Willkommens-Mail geht automatisch raus.
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-navy/10">
        {/* Feb 2026 · 17 Sep 2026 · Bulk-Onboarding-Kadenz — Admin-Trigger
            & Kurz-Zusammenfassung. Wir zeigen die Anzahl offener Serien-
            Schritte (Tag 3/Tag 7 fällig) und einen Button, um den Sweep
            manuell zu starten — auch außerhalb des täglichen Crons. */}
        {isAdmin && (() => {
          const rowsCad = Object.values(cadenceByEmail);
          const day3Due = rowsCad.filter((c) => c.day3_due).length;
          const day7Due = rowsCad.filter((c) => c.day7_due).length;
          const inSeries = rowsCad.filter((c) => c.welcome_sent_at).length;
          if (!inSeries) return null;
          return (
            <div
              className="flex items-center justify-between gap-3 flex-wrap px-6 pt-5 pb-3 border-b border-navy/5"
              data-testid="onboarding-cadence-bar"
            >
              <div className="flex items-center gap-2 flex-wrap text-[12.5px] text-navy-dark/85">
                <span className="label-caps" style={{ color: BRAND_COLORS.gold }}>Onboarding-Serie</span>
                <span className="text-navy-dark/60">·</span>
                <span data-testid="onboarding-cadence-summary-in-series">{inSeries} in Serie</span>
                {(day3Due > 0 || day7Due > 0) && (
                  <>
                    <span className="text-navy-dark/60">·</span>
                    <span data-testid="onboarding-cadence-summary-day3-due">
                      Tag 3 fällig: <b className="tabular-nums">{day3Due}</b>
                    </span>
                    <span className="text-navy-dark/60">·</span>
                    <span data-testid="onboarding-cadence-summary-day7-due">
                      Tag 7 fällig: <b className="tabular-nums">{day7Due}</b>
                    </span>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={dispatchOnboarding}
                disabled={cadenceBusy}
                data-testid="onboarding-cadence-dispatch-btn"
                className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-ivory px-4 py-1.5 text-[11px] tracking-[0.18em] uppercase transition-colors disabled:opacity-60"
              >
                {cadenceBusy && <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.8} />}
                Sweep jetzt starten
              </button>
            </div>
          );
        })()}
        {/* Feb 2026 · DSGVO / Gast-Tier Phase 3 (5 Sep 2026) · Status-Filter-Chips */}
        <div
          className="flex items-center gap-2 flex-wrap px-6 pt-5 pb-3 border-b border-navy/5"
          data-testid="allowed-status-filter"
        >
          {(["all", "guest", "subscriber", "expired"]).map((k) => {
            const active = statusFilter === k;
            const meta = STATUS_META[k];
            return (
              <button
                key={k}
                type="button"
                onClick={() => setStatusFilter(k)}
                data-testid={`allowed-filter-${k}`}
                data-active={active ? "1" : "0"}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase border transition-colors"
                style={{
                  background: active ? meta.color : "transparent",
                  borderColor: active ? meta.color : withAlpha(BRAND_COLORS.navy, 0.2),
                  color: active ? BRAND_COLORS.ivory : BRAND_COLORS.navyDark,
                }}
              >
                {meta.label}
                <span
                  className="px-1.5 py-0.5 text-[10px]"
                  style={{
                    background: active ? withAlpha(BRAND_COLORS.ivory, 0.2) : withAlpha(BRAND_COLORS.navy, 0.08),
                  }}
                >
                  {counts[k]}
                </span>
              </button>
            );
          })}
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-navy/10">
              <th className="label-caps text-left px-6 py-4">{t.team.email}</th>
              <th className="label-caps text-left px-6 py-4">{t.team.name}</th>
              <th className="label-caps text-left px-6 py-4">{t.team.role}</th>
              <th className="label-caps text-left px-6 py-4">Zuletzt eingeloggt</th>
              <th className="label-caps text-left px-6 py-4">{t.team.actions}</th>
            </tr>
          </thead>
          <tbody>
            {filteredList.map((r) => {
              const locked = isLastAdmin(r);
              // Feb 2026 · Gast-Badge + Upgrade-Button (Phase 3, 5 Sep 2026)
              const isGuest = r.status === "guest";
              const isExpired = r.status === "expired";
              const isSubscriber = r.status === "subscriber";
              return (
                <tr
                  key={r.id}
                  className="border-b border-navy/5 last:border-b-0"
                  data-testid={`allowed-row-${r.email}`}
                  data-last-admin={locked ? "true" : "false"}
                >
                  <td className="px-6 py-4 text-navy font-medium">
                    <div className="truncate">
                      {r.role === "partner" && r.user_id ? (
                        <Link
                          to={`/team/partners/${r.user_id}`}
                          data-testid={`allowed-partner-detail-link-${r.email}`}
                          className="text-navy hover:text-gold underline decoration-navy/30 hover:decoration-gold"
                        >
                          {r.email}
                        </Link>
                      ) : (
                        r.email
                      )}
                    </div>
                    {(() => {
                      const linkedList =
                        r.role === "family"
                          ? mainCustomersMap.family?.[r.email]?.main_customers
                          : r.role === "partner"
                          ? mainCustomersMap.partners?.[r.email]?.main_customers
                          : null;
                      if (!linkedList || linkedList.length === 0) return null;
                      return (
                        <div
                          className="mt-1 text-[11px] text-navy-dark/60 leading-snug"
                          data-testid={`allowed-main-customer-${r.email}`}
                        >
                          <span className="opacity-70">
                            {r.role === "family" ? "Zu Hauptkunde:" : "Verkn\u00fcpft mit:"}{" "}
                          </span>
                          {linkedList.map((mc, i) => (
                            <span key={mc.id}>
                              {i > 0 && <span className="opacity-40">{" · "}</span>}
                              <Link
                                to={`/team/customers/${mc.id}`}
                                className="text-navy underline decoration-navy/30 hover:decoration-gold hover:text-gold"
                                data-testid={`allowed-main-customer-link-${r.email}-${mc.id}`}
                              >
                                {mc.name || mc.email}
                              </Link>
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 text-navy-dark/70">{r.name || "—"}</td>
                  <td className="px-6 py-4">
                    {isAdmin ? (
                      <>
                        <select
                          data-testid={`allowed-role-change-${r.email}`}
                          value={r.role}
                          disabled={locked || r.role === "partner"}
                          title={
                            locked
                              ? "Mindestens 2 Admins muessen im System bleiben."
                              : r.role === "partner"
                              ? "Partner-Zugaenge koennen nicht per Umschalter geaendert werden."
                              : undefined
                          }
                          onChange={(e) => {
                            const newRole = e.target.value;
                            const oldRole = r.role;
                            if (newRole === oldRole) return;
                            // Feb 2026 · 6 Sep 2026 · User-Directive „Admin ↔ Concierge erlaubt":
                            // Nur zwischen Kunde/Familie ODER zwischen Admin/Concierge.
                            // Partner ist gesperrt (disabled oben).
                            const teamRoles = ["admin","concierge"];
                            const custRoles = ["customer","family"];
                            const prevTeam = teamRoles.includes(oldRole);
                            const newTeam = teamRoles.includes(newRole);
                            const prevCust = custRoles.includes(oldRole);
                            const newCust = custRoles.includes(newRole);
                            if (prevTeam && newCust) {
                              toast.error("Team-Rollen koennen nicht zu Kunde/Familie herabgestuft werden. Zugang entfernen und neu anlegen.");
                              e.target.value = oldRole; return;
                            }
                            if (prevCust && newTeam) {
                              toast.error("Team-Rollen koennen nicht aus Kunde/Familie entstehen. Neuen Team-Zugang anlegen.");
                              e.target.value = oldRole; return;
                            }
                            if (oldRole === "partner" || newRole === "partner") {
                              toast.error("Partner kann nicht per Umschalter geaendert werden.");
                              e.target.value = oldRole; return;
                            }
                            // Last-2-Admins-Schutz (client-seitig)
                            if (locked) {
                              toast.error("Mindestens 2 Admins muessen im System bleiben.");
                              e.target.value = oldRole;
                              return;
                            }
                            setPinModal({ row: r, newRole, oldRole });
                          }}
                          className={`bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-1.5 text-[12px] text-navy tracking-wider uppercase ${
                            (locked || r.role === "partner") ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                        >
                          {/* Feb 2026 · 6 Sep 2026 · Options-Filter je nach aktueller Rolle:
                              admin/concierge → Admin+Concierge Toggle
                              customer/family → Kunde+Familie Toggle
                              partner → nur Partner (locked) */}
                          {["admin","concierge"].includes(r.role) ? (
                            <>
                              <option value="admin">Admin</option>
                              <option value="concierge">Concierge</option>
                            </>
                          ) : r.role === "partner" ? (
                            <option value="partner">Partner</option>
                          ) : (
                            <>
                              <option value="customer">Kunde</option>
                              <option value="family">Familie</option>
                            </>
                          )}
                        </select>
                        {locked && (
                          <div
                            className="text-[10.5px] mt-1 leading-tight italic"
                            style={{ color: BRAND_COLORS.gold }}
                            data-testid={`allowed-last-admin-hint-${r.email}`}
                          >
                            Letzter Admin — Rolle & Loeschen gesperrt (mind. 2 Admins).
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="label-caps">{r.role}</span>
                    )}
                    {r.role_changed_at && (
                      <div
                        className="text-[10.5px] text-navy-dark/55 italic mt-1.5 leading-tight"
                        title={`Vorherige Rolle: ${r.previous_role || "—"}`}
                        data-testid={`allowed-role-history-${r.email}`}
                      >
                        geändert am {deDateShortTime(r.role_changed_at)}
                        {r.role_changed_by_name && (
                          <span className="text-navy-dark/45"> · von {r.role_changed_by_name}</span>
                        )}
                      </div>
                    )}
                    {/* Feb 2026 · DSGVO / Gast-Tier Phase 3 · Status-Badge */}
                    {(isGuest || isExpired || isSubscriber) && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {isGuest && (
                          <span
                            data-testid={`allowed-status-badge-guest-${r.email}`}
                            className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase"
                            style={{
                              background: withAlpha(BRAND_COLORS.gold, 0.15),
                              color: BRAND_COLORS.goldDark,
                              border: `1px solid ${withAlpha(BRAND_COLORS.gold, 0.5)}`,
                            }}
                          >
                            Gast
                          </span>
                        )}
                        {isExpired && (
                          <span
                            data-testid={`allowed-status-badge-expired-${r.email}`}
                            className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase"
                            style={{
                              background: "rgba(139,30,30,0.1)",
                              color: "#8B1E1E",
                              border: "1px solid rgba(139,30,30,0.4)",
                            }}
                          >
                            Abgelaufen
                          </span>
                        )}
                        {isSubscriber && (
                          <span
                            data-testid={`allowed-status-badge-subscriber-${r.email}`}
                            className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase text-navy-dark/70"
                          >
                            Abonnent
                          </span>
                        )}
                        {r.is_finance_admin && (r.role === "admin" || r.role === "concierge") && (
                          <span
                            data-testid={`allowed-status-badge-finance-${r.email}`}
                            title={r.is_owner ? "Owner ist implizit Finance-Admin" : "Finance-Admin (§147 AO Rechnungsarchiv)"}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase"
                            style={{
                              background: withAlpha(BRAND_COLORS.gold, 0.12),
                              color: BRAND_COLORS.goldDark,
                              border: `1px solid ${withAlpha(BRAND_COLORS.gold, 0.5)}`,
                            }}
                          >
                            <ShieldCheck className="w-3 h-3" strokeWidth={1.8} /> Finance
                          </span>
                        )}
                        {r.deletion_pending && (
                          <span
                            data-testid={`allowed-status-badge-deletion-${r.email}`}
                            title={`Endgültige Löschung am ${r.deletion_execute_at ? deDateFull(r.deletion_execute_at) : "in 30 Tagen"}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase"
                            style={{
                              background: "rgba(139,30,30,0.1)",
                              color: "#8B1E1E",
                              border: "1px solid rgba(139,30,30,0.4)",
                            }}
                          >
                            <Clock className="w-3 h-3" strokeWidth={1.8} />
                            Wird gelöscht am {r.deletion_execute_at ? deDateMedium(r.deletion_execute_at) : "—"}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Feb 2026 · 17 Sep 2026 · Onboarding-Kadenz-Chips
                        (Tag 0 · Tag 3 · Tag 7). Rendert AUSSERHALB der
                        Status-Badge-Conditional, damit auch Admin- und
                        Concierge-Rows die Serie sehen. Sichtbar nur wenn
                        `welcome_sent_at` gesetzt ist. */}
                    {(() => {
                      const cad = cadenceByEmail[r.email];
                      if (!cad || !cad.welcome_sent_at) return null;
                      const chipStyle = (bg, fg, border) => ({
                        background: bg, color: fg, border: `1px solid ${border}`,
                      });
                      const okBg = withAlpha(BRAND_COLORS.gold, 0.15);
                      const okBorder = withAlpha(BRAND_COLORS.gold, 0.5);
                      const dueBg = "rgba(196,120,20,0.12)";
                      const dueBorder = "rgba(196,120,20,0.55)";
                      const dueFg = "#7A4A0F";
                      const pendingBg = withAlpha(BRAND_COLORS.navy, 0.06);
                      const pendingFg = BRAND_COLORS.navyDark;
                      const pendingBorder = withAlpha(BRAND_COLORS.navy, 0.18);
                      return (
                        <div
                          className="mt-2 inline-flex items-center gap-1 flex-wrap"
                          data-testid={`allowed-onboarding-cadence-${r.email}`}
                          data-day3-status={cad.day3_sent_at ? "sent" : cad.day3_due ? "due" : "pending"}
                          data-day7-status={cad.day7_sent_at ? "sent" : cad.day7_due ? "due" : "pending"}
                        >
                          <span
                            className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase"
                            style={chipStyle(okBg, BRAND_COLORS.goldDark, okBorder)}
                            title={`Willkommen gesendet am ${deDate(cad.welcome_sent_at)}`}
                          >
                            Tag 0 ✓
                          </span>
                          <span
                            className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase"
                            style={
                              cad.day3_sent_at
                                ? chipStyle(okBg, BRAND_COLORS.goldDark, okBorder)
                                : cad.day3_due
                                  ? chipStyle(dueBg, dueFg, dueBorder)
                                  : chipStyle(pendingBg, pendingFg, pendingBorder)
                            }
                            title={
                              cad.day3_sent_at
                                ? `Erste Schritte gesendet am ${deDate(cad.day3_sent_at)}`
                                : cad.day3_due
                                  ? "Erste-Schritte-Mail ist fällig — nächster Sweep sendet sie"
                                  : "Erste-Schritte-Mail läuft ab Tag 3 nach dem Willkommen"
                            }
                          >
                            Tag 3 {cad.day3_sent_at ? "✓" : cad.day3_due ? "•" : "○"}
                          </span>
                          <span
                            className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase"
                            style={
                              cad.day7_sent_at
                                ? chipStyle(okBg, BRAND_COLORS.goldDark, okBorder)
                                : cad.day7_due
                                  ? chipStyle(dueBg, dueFg, dueBorder)
                                  : chipStyle(pendingBg, pendingFg, pendingBorder)
                            }
                            title={
                              cad.day7_sent_at
                                ? `Feedback gesendet am ${deDate(cad.day7_sent_at)}`
                                : cad.day7_due
                                  ? "Feedback-Mail ist fällig — nächster Sweep sendet sie"
                                  : "Feedback-Mail läuft ab Tag 7 nach dem Willkommen"
                            }
                          >
                            Tag 7 {cad.day7_sent_at ? "✓" : cad.day7_due ? "•" : "○"}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 text-[12px] text-navy-dark/70" data-testid={`allowed-last-login-${r.email}`}>
                    {r.last_login_at
                      ? deDateShortTime(r.last_login_at)
                      : (r.has_signed_in ? "—" : <span style={{ color: BRAND_COLORS.gold }}>Eingeladen</span>)}
                  </td>
                  <td className="px-6 py-4 flex items-center gap-4 flex-wrap">
                    {isAdmin && isGuest && (
                      <div className="flex flex-col gap-1.5" data-testid={`allowed-guest-actions-${r.email}`}>
                        <button
                          data-testid={`allowed-upgrade-${r.email}`}
                          onClick={async () => {
                            if (!(await confirmDialog({
                              title: "Gast zu Abonnent hochstufen?",
                              description: `${r.email} wird auf Rolle "Kunde" und Status "Abonnent" umgestellt. Alle gesperrten Aktionen (Buchungen, Sonderwünsche, Extras, Angebote) werden freigeschaltet. Bestätigung mit Admin-PIN.`,
                              confirmText: "Weiter",
                            }))) return;
                            setPinModal({ row: r, kind: "upgrade" });
                          }}
                          className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-3 py-1.5 border transition-colors"
                          style={{
                            background: withAlpha(BRAND_COLORS.gold, 0.1),
                            borderColor: BRAND_COLORS.gold,
                            color: BRAND_COLORS.goldDark,
                          }}
                        >
                          <ArrowUp className="w-3 h-3" strokeWidth={1.8} />
                          Hochstufen
                        </button>
                        {/* Feb 2026 · 6 Sep 2026 · User-Directive „Kachel Entfernen unter Hochstufen":
                            „Direkt unter der Kachel 'Hochstufen' soll eine neue Kachel 'Entfernen'
                            hinzugefügt werden." Der Entfernen-Button hat den DSGVO Art. 17-Flow
                            (30-Tage-Karenz, Preview-Modal) wie die `allowed-remove-{email}` weiter unten. */}
                        <button
                          data-testid={`allowed-remove-under-upgrade-${r.email}`}
                          onClick={() => remove(r)}
                          className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-3 py-1.5 border transition-colors hover:bg-red-50"
                          style={{
                            borderColor: withAlpha(BRAND_COLORS.navy, 0.25),
                            color: BRAND_COLORS.navyDark,
                          }}
                        >
                          <Trash2 className="w-3 h-3" strokeWidth={1.8} />
                          Entfernen
                        </button>
                      </div>
                    )}
                    {isAdmin && r.role === "customer" && isSubscriber && (
                      <button
                        data-testid={`allowed-downgrade-${r.email}`}
                        onClick={() => setDowngradePreview({ row: r })}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-3 py-1.5 border transition-colors hover:bg-red-50"
                        style={{
                          borderColor: withAlpha(BRAND_COLORS.navy, 0.25),
                          color: BRAND_COLORS.navyDark,
                        }}
                      >
                        <ArrowDown className="w-3 h-3" strokeWidth={1.8} />
                        Herabstufen
                      </button>
                    )}
                    {isAdmin && user?.is_owner_admin && (r.role === "admin" || r.role === "concierge") && !r.is_owner && r.user_id && (
                      <button
                        data-testid={`allowed-finance-toggle-${r.email}`}
                        onClick={async () => {
                          const enabling = !r.is_finance_admin;
                          if (!(await confirmDialog({
                            title: enabling ? "Finance-Admin aktivieren?" : "Finance-Admin deaktivieren?",
                            description: enabling
                              ? `${r.email} erhält das Finance-Admin-Recht. Damit werden archivierte Rechnungen nach §147 AO sichtbar (Konto-Löschungen). Bestätigung mit Admin-PIN.`
                              : `${r.email} verliert das Finance-Admin-Recht. Das Rechnungsarchiv wird für dieses Konto sofort gesperrt. Bestätigung mit Admin-PIN.`,
                            confirmText: "Weiter",
                            variant: enabling ? "primary" : "danger",
                          }))) return;
                          setPinModal({ row: r, kind: "finance-toggle" });
                        }}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-3 py-1.5 border transition-colors"
                        style={{
                          background: r.is_finance_admin ? withAlpha(BRAND_COLORS.gold, 0.15) : "transparent",
                          borderColor: r.is_finance_admin ? BRAND_COLORS.gold : withAlpha(BRAND_COLORS.navy, 0.25),
                          color: r.is_finance_admin ? BRAND_COLORS.goldDark : BRAND_COLORS.navyDark,
                        }}
                      >
                        <ShieldCheck className="w-3 h-3" strokeWidth={1.8} />
                        {r.is_finance_admin ? "Finance an" : "Finance aus"}
                      </button>
                    )}
                    {isAdmin && r.deletion_pending && (
                      <button
                        data-testid={`allowed-reactivate-${r.email}`}
                        onClick={() => reactivate(r)}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-3 py-1.5 border transition-colors hover:bg-gold/5"
                        style={{
                          background: withAlpha(BRAND_COLORS.gold, 0.08),
                          borderColor: BRAND_COLORS.gold,
                          color: BRAND_COLORS.goldDark,
                        }}
                      >
                        <RotateCcw className="w-3 h-3" strokeWidth={1.8} />
                        Reaktivieren
                      </button>
                    )}
                    {isAdmin && !r.last_login_at && !r.deletion_pending && (
                      <button
                        data-testid={`allowed-welcome-${r.email}`}
                        onClick={async () => {
                          try {
                            await api.post("/admin/send-welcome", { email: r.email });
                            toast.success("Willkommens-E-Mail gesendet.");
                          } catch (err) {
                            toast.error(formatErr(err.response?.data?.detail) || err.message);
                          }
                        }}
                        className="text-[11px] tracking-widest uppercase text-navy hover:text-gold border-b border-gold/40 hover:border-gold pb-0.5 transition-colors"
                      >
                        Willkommen senden
                      </button>
                    )}
                    {/* Feb 2026 · 8 Sep 2026 · User-Directive „Kunde nicht direkt entfernen":
                        „Ein Nutzer mit der Rolle 'Kunde' darf nicht direkt entfernt
                        werden. Entfernen ist nur möglich wenn der Nutzer die Rolle
                        'Gast' hat. Der einzig erlaubte Weg ist: Kunde → Gast (mit
                        PIN) → Entfernen." Daher wird die Entfernen-Kachel für
                        Kunden ausgeblendet. Für Gäste rendert die Kachel bereits
                        unter „Hochstufen" (Guest-Actions-Block). Concierge/Partner/
                        Family behalten die direkte Entfernen-Option.
                        8 Sep 2026 · Follow-up-Directive: Der frühere Hinweis
                        „Herabstufung erforderlich" wurde für Kunden entfernt —
                        der Backend-Guard (400) und die fehlende Kachel reichen. */}
                    {isAdmin && r.role !== "admin" && r.role !== "customer" && !isGuest && (
                      <button
                        data-testid={`allowed-remove-${r.email}`}
                        onClick={() => remove(r)}
                        disabled={locked}
                        title={locked ? "Mindestens 2 Admins muessen im System bleiben." : undefined}
                        className={`inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-3 py-1.5 border transition-colors ${
                          locked
                            ? "opacity-40 cursor-not-allowed"
                            : "hover:bg-red-50"
                        }`}
                        style={{
                          borderColor: withAlpha(BRAND_COLORS.navy, 0.25),
                          color: BRAND_COLORS.navyDark,
                        }}
                      >
                        <Trash2 className="w-3 h-3" strokeWidth={1.8} />
                        Entfernen
                      </button>
                    )}
                    {/* Feb 2026 · 6 Sep 2026 · User-Directive „Admin nicht direkt loeschbar":
                        Anstelle des Loeschen-Buttons zeigen wir bei Admins einen
                        stillen Hinweis. Loeschen geht erst nach Herabstufung auf Concierge. */}
                    {isAdmin && r.role === "admin" && (
                      <span
                        data-testid={`allowed-admin-delete-hint-${r.email}`}
                        title="Admins koennen nicht direkt entfernt werden. Erst auf 'Concierge' herabstufen (mit PIN), dann entfernen."
                        className="text-[10.5px] tracking-wider uppercase text-navy-dark/40 italic cursor-help"
                      >
                        Herabstufung erforderlich
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <RoleChangePinModal
        open={!!pinModal}
        customers={customers}
        target={pinModal ? (
          pinModal.kind === "upgrade"
            ? { email: pinModal.row.email, oldRole: "Gast", newRole: "Abonnent" }
            : pinModal.kind === "downgrade"
            ? { email: pinModal.row.email, oldRole: "Abonnent", newRole: "Gast" }
            : pinModal.kind === "finance-toggle"
            ? {
                email: pinModal.row.email,
                oldRole: pinModal.row.is_finance_admin ? "Finance-Admin" : "Kein Finance",
                newRole: pinModal.row.is_finance_admin ? "Kein Finance" : "Finance-Admin",
              }
            : pinModal.kind === "delete"
            ? { email: pinModal.row.email, oldRole: "Aktiv", newRole: "Wird gelöscht (30 Tage)" }
            : pinModal.kind === "reactivate"
            ? { email: pinModal.row.email, oldRole: "In Löschfrist", newRole: "Wieder aktiv" }
            : {
                email: pinModal.row.email,
                oldRole: pinModal.oldRole,
                newRole: pinModal.newRole,
                currentOwnerId: pinModal.row.owner_id || "",
              }
        ) : null}
        onCancel={() => {
          setPinModal(null);
          load(); // revert any dropdown visual state
        }}
        onSubmit={submitRoleChange}
      />
      <DeletionPreviewModal
        open={!!previewModal}
        row={previewModal?.row}
        onCancel={() => setPreviewModal(null)}
        onContinue={async (preview) => {
          // Never-logged-in-Zugaenge: der Backend-Endpoint entfernt sofort
          // ohne Karenz — trotzdem PIN einholen fuer Konsistenz.
          const row = previewModal.row;
          setPreviewModal(null);
          setPinModal({ row, kind: "delete" });
        }}
      />
      <DowngradePreviewModal
        open={!!downgradePreview}
        row={downgradePreview?.row}
        onCancel={() => setDowngradePreview(null)}
        onContinue={() => {
          const row = downgradePreview.row;
          setDowngradePreview(null);
          setPinModal({ row, kind: "downgrade" });
        }}
      />
      <ReactivateRestoreModal
        open={!!restoreModal}
        row={restoreModal?.row}
        onSkip={() => setRestoreModal(null)}
        onDone={() => { setRestoreModal(null); load(); }}
      />
    </div>
  );
}
